import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as fragments from '~/apollo/fragments';
import type {
  StudyCrossSectionsRouteQuery,
  StudyCrossSectionsRouteQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { useStudyOutletContext } from '~/routes/study/$studyId';

const STUDY_CROSS_SECTIONS_ROUTE = gql`
  query StudyCrossSectionsRoute($studyId: Int!) {
    studyList(id: $studyId) {
      ...studyParts
      crossSections {
        ...crossSectionParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
          }
        }
        files {
          ...fileParts
        }
        georeference {
          ...georeferenceParts
        }
      }
    }
  }

  ${fragments.studyParts}
  ${fragments.crossSectionParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
  ${fragments.georeferenceParts}
`;

export default function StudyCrossSectionsRoute() {
  const ctx = useStudyOutletContext();
  const studyId = ctx.study.id;

  const { data, loading } = useQuery<
    StudyCrossSectionsRouteQuery,
    StudyCrossSectionsRouteQueryVariables
  >(STUDY_CROSS_SECTIONS_ROUTE, { variables: { studyId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: STUDY_CROSS_SECTIONS_ROUTE, variables: { studyId } },
  ];

  const crossSections =
    data?.studyList.find(s => s.id === studyId)?.crossSections ?? [];

  if (loading) return <SpinnerPlaceholder />;

  return (
    <SupportingObjectList
      items={crossSections}
      soParentType="study"
      soParentId={studyId}
      bookmarkParentType={BookmarkParentType.CrossSection}
      refetchQueries={refetchQueries}
    />
  );
}
